import { IBaseCartGoodsItem } from '@/api/types/cart';
import { Currency, IPrice, User } from '@/api/types/public';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { add } from '@/utils/math';
import { ZL_PRESCRIPTION_DATA } from './constants';
import { CoatingItem, ColorItem, LensIndexItem } from './types';

// 数字小数点后补齐2位
export const getToFix2NumArr = (arr: string[]) => {
  return arr.map((item) => {
    const a = item.split('.');
    a.length === 1 ? a.push('00') : a[1].length === 1 && (a[1] += '0');
    return `+${a.join('.')}`;
  });
};

// 生成等差数组
export const getEqualDiffArr = (start: number, end: number, difference: number) => {
  if (((end - start) / difference) % 1 > 0) {
    return [];
  }
  return new Array((end - start) / difference + 1).fill(0).map((_, index) => {
    return String(start + difference * index);
  });
};

/**
 * @description: 价格计算处理
 * hasIndexPrice 为真时（frame only 或者 non-prescription 或者 sunglasses non-prescription）
 * 价格为 “镜片类型组合价” + “镜片折射率组合价”，其中镜片折射率组合价是，color_list下的lens_list的第一项中的index_price_list
 * 否则，价格为 “镜片类型价”
 * @return {*}
 */
export const handleColorItemPriceInfo = (
  colorItem: ColorItem,
  hasIndexPrice = false,
  currency: Currency,
  userInfo: User,
) => {
  let lensPriceList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    colorItem[`lens_type_price_list_v${userInfo.member_level}`] &&
    colorItem[`discount_percent_v${userInfo.member_level}`] > 0
  ) {
    lensPriceList = colorItem[`lens_type_price_list_v${userInfo.member_level}`];
  } else {
    lensPriceList = colorItem.lens_type_price_list as IPrice[];
  }
  const lensGenPrice = lensPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: lensGenPrice.price,
    sell_price: lensGenPrice.sell_price,
  };
  if (Array.isArray(colorItem.lens_list) && colorItem.lens_list.length && hasIndexPrice) {
    const indexPrice = colorItem.lens_list[0]; // 镜片折射率组合价
    let indexPriceList: IPrice[] = [];
    if (
      userInfo?.is_member === 1 &&
      indexPrice[`index_price_list_v${userInfo.member_level}`] &&
      indexPrice[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      indexPriceList = indexPrice[`index_price_list_v${userInfo.member_level}`];
    } else {
      indexPriceList = indexPrice.index_price_list as IPrice[];
    }
    const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片折射率组合价
    const lensAddIndexPriceInfo = {
      currency: currency.name,
      price: add(Number(lensGenPrice.price), Number(indexGenPrice.price)).toFixed(2),
      sell_price: add(Number(lensGenPrice.sell_price), Number(indexGenPrice.sell_price)).toFixed(2),
    };
    return lensAddIndexPriceInfo; // 返回相加之后的价格
  }
  return priceInfo;
};

/**
 * 获取最小镜片价格所在的颜色item
 * @param colorList 镜片列表
 * @param hasIndexPrice 是否包含折射率价格
 * @param userInfo 用户信息
 * @returns 最小镜片价格
 */
export const getMinColorItem = (colorList: ColorItem[], hasIndexPrice = false, userInfo: User) => {
  let colorItemIndex = 0;
  if (hasIndexPrice) {
    colorItemIndex = minPrice(colorList, userInfo, 'lens_type_price_list', 'index_price_list') || 0;
  } else {
    colorItemIndex = minPrice(colorList, userInfo, 'lens_type_price_list') || 0;
  }

  return colorItemIndex === -1 ? {} : colorList[colorItemIndex] || {};
};

/**
 * 最小镜片价格下标
 */
export const minPrice = (itemList: ColorItem[], userInfo: User, key: string, key2?: string) => {
  if (itemList.length === 0) {
    return -1;
  }

  let minIndex = 0;
  let minValue = itemList[0];
  if (userInfo.member_level > -1) {
    key = `${key}_v${userInfo.member_level}`;
    if (key2) {
      key2 = `${key2}_v${userInfo.member_level}`;
    }
  }

  for (let i = 1; i < itemList.length; i++) {
    let curr = parseFloat(itemList[i][key][0].sell_price);
    let min = parseFloat(minValue[key][0].sell_price);
    if (key2) {
      curr += parseFloat(itemList[i].lens_list[0][key2][0].sell_price);
      min += parseFloat(minValue.lens_list[0][key2][0].sell_price);
    }
    if (curr < min) {
      minIndex = i;
      minValue = itemList[i];
    }
  }
  return minIndex;
};

/**
 * @description: 折扣 discount_percent计算处理
 * frame only 或者 non-prescription 或者 sunglasses non-prescription时
 * 价格为 “镜片类型组合价” + “镜片折射率组合价”，其中镜片折射率组合价是，color_list下的lens_list的第一项中的index_price_list
 * （（lensType原价 + lensIndex原价） - （lensType促销 + lensIndex促销））/ (lensType原价 + lensIndex原价 )
 * @return {*}
 */
export const getDiscountPercent = (
  colorItem: ColorItem,
  hasIndexPrice = false,
  currency: Currency,
  userInfo: User,
  isWap = false,
) => {
  if (hasIndexPrice) {
    let lensPriceList: IPrice[] = [];
    if (
      userInfo.is_member === 1 &&
      colorItem[`lens_type_price_list_v${userInfo.member_level}`] &&
      colorItem[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      lensPriceList = colorItem[`lens_type_price_list_v${userInfo.member_level}`];
    } else {
      lensPriceList = colorItem.lens_type_price_list as IPrice[];
    }
    const lensGenPrice = lensPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
    // （（lensType原价 + lensIndex原价） - （lensType促销 + lensIndex促销））/ (lensType原价 + lensIndex原价 )
    if (Array.isArray(colorItem.lens_list) && colorItem.lens_list.length) {
      const indexPrice = colorItem.lens_list[0]; // 镜片折射率组合价
      let indexPriceList: IPrice[] = [];
      if (
        userInfo.is_member === 1 &&
        indexPrice[`index_price_list_v${userInfo.member_level}`] &&
        indexPrice[`discount_percent_v${userInfo.member_level}`] > 0
      ) {
        indexPriceList = indexPrice[`index_price_list_v${userInfo.member_level}`];
      } else {
        indexPriceList = indexPrice.index_price_list as IPrice[];
      }
      const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片折射率组合价
      const lensAddIndexPriceInfo = {
        price: add(Number(lensGenPrice.price), Number(indexGenPrice.price)).toFixed(2),
        sell_price: add(Number(lensGenPrice.sell_price), Number(indexGenPrice.sell_price)).toFixed(
          2,
        ),
      };
      const discountPercent = Math.round(
        ((Number(lensAddIndexPriceInfo.price) - Number(lensAddIndexPriceInfo.sell_price)) /
          Number(lensAddIndexPriceInfo.price)) *
          100,
      );
      return discountPercent;
    }
  }
  if (userInfo.is_member === 1) {
    return colorItem[`display_discount_percentage_v${userInfo.member_level}`] || 0;
  } else {
    return isWap ? colorItem.discount_percent?.wap || 0 : colorItem.discount_percent?.pc || 0;
  }
};

// 是否应用了会员折扣
export const hasColorItemMemberPrice = (
  colorItem: ColorItem,
  userInfo: User,
  hasIndexPrice = false,
) => {
  let hasColorPrice = false;
  let hasLensIndex = false;
  if (userInfo.member_level === -1) {
    return false;
  }

  // 判断镜片类型是否有会员价
  if (colorItem && colorItem[`discount_percent_v${userInfo.member_level}`] > 0) {
    hasColorPrice = true;
  }

  if (hasIndexPrice && colorItem) {
    const lensIndexItem = colorItem.lens_list[0];
    if (lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0) {
      hasLensIndex = true;
    }
    return hasColorPrice || hasLensIndex;
  }

  return hasColorPrice;
};

/**
 * 处理镜片折射率价格信息
 * @param lensIndexItem 镜片折射率项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleLensIndexPriceInfo = (
  lensIndexItem: LensIndexItem,
  currency: Currency,
  userInfo: User,
) => {
  let indexPriceList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    lensIndexItem[`index_price_list_v${userInfo.member_level}`]
  ) {
    indexPriceList = lensIndexItem[`index_price_list_v${userInfo.member_level}`];
  } else {
    indexPriceList = lensIndexItem.index_price_list as IPrice[];
  }
  const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: indexGenPrice.price,
    sell_price: indexGenPrice.sell_price,
  };
  return priceInfo;
};

/**
 * 处理镜片折射率价格折扣
 * @param lensIndexItem 镜片折射率项
 * @param userInfo 用户信息
 * @returns 价格折扣
 */
export const handleLensIndexDiscountPercent = (lensIndexItem: LensIndexItem, userInfo: User) => {
  if (lensIndexItem.index_price_discount_type === 1) {
    if (
      userInfo.is_member === 1 &&
      lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`] &&
      lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`] > 0
    ) {
      return lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`];
    } else {
      return lensIndexItem.display_discount_percentage || 0;
    }
  } else if (
    userInfo.is_member === 1 &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0
  ) {
    return lensIndexItem[`discount_percent_v${userInfo.member_level}`];
  } else {
    return 0;
  }
};

/**
 * 处理镀膜价格信息
 * @param coatingItem 镀膜项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleCoatingPriceInfo = (
  coatingItem: CoatingItem,
  currency: Currency,
  userInfo: User,
) => {
  let priceInfoList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    coatingItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    coatingItem[`price_info_v${userInfo.member_level}`]
  ) {
    priceInfoList = coatingItem[`price_info_v${userInfo.member_level}`];
  } else {
    priceInfoList = coatingItem.price_info as IPrice[];
  }
  const priceInfoItem = priceInfoList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: priceInfoItem.price,
    sell_price: priceInfoItem.sell_price,
  };
  return priceInfo;
};
// 镀膜是否应用了会员折扣
export const hasCoatingMemberPrice = (coatingItem: CoatingItem, userInfo: User) => {
  let hasColorPrice = false;
  if (userInfo.member_level === -1) {
    return false;
  }

  // 判断镜片类型是否有会员价
  if (coatingItem && coatingItem[`discount_percent_v${userInfo.member_level}`] > 0) {
    hasColorPrice = true;
  }

  return hasColorPrice;
};

/**
 * 处理镀膜价格折扣
 * @param coatingItem 镀膜项
 * @param userInfo 用户信息
 * @returns 价格折扣
 */
export const handleCoatingDiscountPercent = (coatingItem: CoatingItem, userInfo: User) => {
  if (userInfo.is_member === 1 && coatingItem[`discount_percent_v${userInfo.member_level}`]) {
    return coatingItem[`discount_percent_v${userInfo.member_level}`];
  }
  return 0;
};

/**
 * 处理商品镜框价格信息
 * @param coatingItem 镀膜项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleGoodsFramePriceInfo = (
  goodsFrameItem: IBaseCartGoodsItem['product'] & {
    point_price_info?: IPrice[];
  },
  isRedeem = false,
  currency: Currency,
  userInfo: User,
) => {
  let priceInfoList: IPrice[] = [];
  if (isRedeem && goodsFrameItem.point_price_info) {
    priceInfoList = goodsFrameItem.point_price_info;
  } else if (
    userInfo?.is_member === 1 &&
    goodsFrameItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    goodsFrameItem[`price_info_v${userInfo.member_level}`]
  ) {
    priceInfoList = goodsFrameItem[`price_info_v${userInfo.member_level}`];
  } else {
    priceInfoList = goodsFrameItem.price_info as IPrice[];
  }
  const priceInfoItem = priceInfoList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: priceInfoItem.price,
    sell_price: priceInfoItem.sell_price,
  };
  return priceInfo;
};

/**
 * 处理处方数据
 * @param data 前端处方数据
 * @returns 可提交给接口的处方数据
 */
export const handlerPrescriptionData = (data) => {
  const presData = { ...data };
  const option = {};
  if (presData.option) {
    const excludeKey = [
      'coating_id',
      'lens_id',
      'old_lens_id',
      'pd',
      'pd_l',
      'pd_r',
      'od_axis',
      'os_axis',
      'name',
      'old_lens_price',
      'old_lens_status',
    ]; // 不需要转换
    const keys = Object.keys(presData.option);
    for (const key of keys) {
      if (isNaN(parseFloat(presData.option[key])) || excludeKey.includes(key)) {
        option[key] = `${presData.option[key]}`;
      } else {
        option[key] = `${parseFloat(presData.option[key]).toFixed(2)}`;
      }
    }
    presData.option = option;
  }
  return presData;
};

export const removePrescriptionPlus = (prescription: IPrescriptionOptions) => {
  const excludeKey = [
    'coating_id',
    'lens_id',
    'old_lens_id',
    'pd',
    'pd_l',
    'pd_r',
    'od_axis',
    'os_axis',
    'name',
    'old_lens_price',
    'old_lens_status',
  ]; // 不需要转换
  const keys = Object.keys(prescription);
  for (const key of keys) {
    if (isNaN(parseFloat(prescription[key] as string)) || excludeKey.includes(key)) {
      prescription[key] = `${prescription[key]}`;
    } else {
      prescription[key] = `${parseFloat(prescription[key] as string).toFixed(2)}`;
    }
  }
  return prescription;
};

export const getApiPrescriptionType = (prescriptionType: EPrescriptionType) => {
  return [EPrescriptionType.READERS, EPrescriptionType.PRESCRIPTION_READING].includes(
    prescriptionType,
  )
    ? EPrescriptionType.READING_GLASSES
    : prescriptionType;
};

// 比较当前的处方和识别出的处方是否相同
export const preIsSame = (newPres: IPrescriptionOptions, oldPres: IPrescriptionOptions) => {
  const defaultPres = {
    od_sph: '0.00',
    os_sph: '0.00',
    od_cyl: '0.00',
    os_cyl: '0.00',
    od_axis: 'None',
    os_axis: 'None',
    od_add: '0.00',
    os_add: '0.00',
    // 两组PD
    hasTwoPd: false,
    pd: '',
    pd_l: '',
    pd_r: '',
    // 是否具有斜视数据
    hasStrabismus: false,
    od_pv: '0.00',
    od_bd: '',
    os_pv: '0.00',
    os_bd: '',
    od_pv_r: '0.00',
    od_bd_r: '',
    os_pv_r: '0.00',
    os_bd_r: '',
    saveMyPres: false,
    name: '',
    prescriptions_image: '', // 处方单图片
  };
  const keysNewPres = [
    'od_sph',
    'os_sph',
    'od_cyl',
    'os_cyl',
    'od_axis',
    'os_axis',
    'od_add',
    'os_add',
    'pd',
    'pd_l',
    'pd_r',
    // 'od_pv', 'od_bd', 'os_pv', 'os_bd', 'od_pv_r', 'od_bd_r', 'os_pv_r', 'os_bd_r',
  ];
  // 检查每个键的值是否相同
  for (const key of keysNewPres) {
    if (
      newPres[key] !== oldPres[key] &&
      +newPres[key] !== +oldPres[key] &&
      oldPres[key] !== defaultPres[key]
    ) {
      return false;
    }
  }
  return true;
};

// 获取缓存的成品处方数据
export const getReaderCachePres = () => {
  if (typeof window === 'undefined') return '';

  const key = ZL_PRESCRIPTION_DATA;
  const cacheValue = localStorage.getItem(key);
  try {
    if (cacheValue) {
      const data = JSON.parse(cacheValue);
      if (data[EPrescriptionType.READERS]) {
        const sph = data[EPrescriptionType.READERS].od_sph;
        return sph;
      }
    }
  } catch (e) {
    localStorage.removeItem(key);
    logger.warn('👀 - getCachePres', e);
  }
  return '';
};
// 设置成品老化处方缓存
export const setReaderCachePres = (prescription: {
  od_sph: string;
  os_sph: string;
  presType: string;
}) => {
  if (typeof window === 'undefined') return;

  const key = ZL_PRESCRIPTION_DATA;
  const cacheValue = localStorage.getItem(key);
  try {
    const data = cacheValue ? JSON.parse(cacheValue) : {};
    data[EPrescriptionType.READERS] = prescription;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    localStorage.removeItem(key);
    logger.warn('👀 - setCachePres', e);
  }
};
